import { createSlice } from "@reduxjs/toolkit";
import {
  createPrescription,
  classifyAndSavePrescriptionDocuments,
  fetchAllPrescriptions,
  fetchPatientPrescriptions, // patient-self
  fetchPrescriptionsByPatientForDoctor, // ✅ doctor -> patient list
  updatePrescription,
  deletePrescription,
  generatePrescriptionPdf,
  fetchPrescriptionByConsultation, // ✅ per consultation
} from "../thunks/prescriptionThunks";

const initialState = {
  lastCreated: null,
  lastClassifiedSave: null,
  classifiedSaveStatus: "idle",
  classifiedSaveError: null,

  // liste globale (vue médecin - toutes)
  prescriptions: [],
  prescriptionsStatus: "idle",
  prescriptionsError: null,

  // liste vue patient-self
  patientPrescriptions: [],
  patientPrescriptionsStatus: "idle",
  patientPrescriptionsError: null,

  // ✅ prescriptions d’un patient (vue médecin)
  doctorPatientPrescriptions: [],
  doctorPatientPrescriptionsStatus: "idle",
  doctorPatientPrescriptionsError: null,

  // ✅ prescription fetchée (dernière) pour une consultation (utilisable si tu veux)
  consultationPrescription: null,
  consultationPrescriptionStatus: "idle",
  consultationPrescriptionError: null,

  // ✅ NEW: cache par consultationId (pour afficher sous chaque consultation)
  byConsultation: {}, // { [consultationId]: prescription | null }

  // état global (create/update/delete/pdf)
  status: "idle",
  error: null,

  // templates
  template: null,
  templateStatus: "idle",
  templateError: null,
};

const prescriptionSlice = createSlice({
  name: "prescription",
  initialState,
  reducers: {
    resetPrescriptionState: () => initialState,

    clearError: (state) => {
      state.error = null;
      state.prescriptionsError = null;
      state.patientPrescriptionsError = null;
      state.doctorPatientPrescriptionsError = null;
      state.consultationPrescriptionError = null;
      state.templateError = null;
      state.classifiedSaveError = null;
    },

    clearLastCreated: (state) => {
      state.lastCreated = null;
    },

    clearLastClassifiedSave: (state) => {
      state.lastClassifiedSave = null;
      state.classifiedSaveStatus = "idle";
      state.classifiedSaveError = null;
    },

    // ✅ optionnel: vider une clé consultation précise si tu veux
    clearConsultationPrescriptionCache: (state, action) => {
      const cid = String(action.payload || "").trim();
      if (cid) delete state.byConsultation[cid];
    },
  },

  extraReducers: (builder) => {
    // =====================================================
    // CREATE
    // =====================================================
    builder
      .addCase(createPrescription.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createPrescription.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastCreated = action.payload || null;

        const created = action.payload;

        // push dans liste globale
        if (created) state.prescriptions.unshift(created);

        // push doctor->patient list
        if (created?.patientId) {
          state.doctorPatientPrescriptions.unshift(created);
        }

        // ✅ NEW: si la prescription est liée à une consultation, on cache par consultationId
        if (created?.consultationId) {
          state.byConsultation[String(created.consultationId)] = created;
        }
      })
      .addCase(createPrescription.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la création de l'ordonnance";
      });

    // =====================================================
    // CLASSIFY + SAVE
    // =====================================================
    builder
      .addCase(classifyAndSavePrescriptionDocuments.pending, (state) => {
        state.status = "loading";
        state.classifiedSaveStatus = "loading";
        state.error = null;
        state.classifiedSaveError = null;
      })
      .addCase(classifyAndSavePrescriptionDocuments.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.classifiedSaveStatus = "succeeded";
        state.lastClassifiedSave = action.payload || null;

        const prescription = action.payload?.saved?.prescription;
        if (prescription?.status === "saved" && prescription?.id) {
          state.lastCreated = {
            id: prescription.id,
            description: prescription.text,
            consultationId: action.meta?.arg?.consultationId || null,
            patientId: action.meta?.arg?.patientId || null,
          };
        }
      })
      .addCase(classifyAndSavePrescriptionDocuments.rejected, (state, action) => {
        state.status = "failed";
        state.classifiedSaveStatus = "failed";
        state.classifiedSaveError =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la classification et sauvegarde";
        state.error = state.classifiedSaveError;
      });

    // =====================================================
    // FETCH ALL (doctor)
    // =====================================================
    builder
      .addCase(fetchAllPrescriptions.pending, (state) => {
        state.prescriptionsStatus = "loading";
        state.prescriptionsError = null;
      })
      .addCase(fetchAllPrescriptions.fulfilled, (state, action) => {
        state.prescriptionsStatus = "succeeded";
        state.prescriptions = action.payload || [];
      })
      .addCase(fetchAllPrescriptions.rejected, (state, action) => {
        state.prescriptionsStatus = "failed";
        state.prescriptionsError =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la récupération des prescriptions";
      });

    // =====================================================
    // ✅ FETCH BY PATIENT FOR DOCTOR
    // =====================================================
    builder
      .addCase(fetchPrescriptionsByPatientForDoctor.pending, (state) => {
        state.doctorPatientPrescriptionsStatus = "loading";
        state.doctorPatientPrescriptionsError = null;
      })
      .addCase(fetchPrescriptionsByPatientForDoctor.fulfilled, (state, action) => {
        state.doctorPatientPrescriptionsStatus = "succeeded";
        state.doctorPatientPrescriptions = action.payload || [];
      })
      .addCase(fetchPrescriptionsByPatientForDoctor.rejected, (state, action) => {
        state.doctorPatientPrescriptionsStatus = "failed";
        state.doctorPatientPrescriptionsError =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la récupération des prescriptions du patient (doctor)";
      });

    // =====================================================
    // PATIENT SELF
    // =====================================================
    builder
      .addCase(fetchPatientPrescriptions.pending, (state) => {
        state.patientPrescriptionsStatus = "loading";
        state.patientPrescriptionsError = null;
      })
      .addCase(fetchPatientPrescriptions.fulfilled, (state, action) => {
        state.patientPrescriptionsStatus = "succeeded";
        state.patientPrescriptions = action.payload || [];
      })
      .addCase(fetchPatientPrescriptions.rejected, (state, action) => {
        state.patientPrescriptionsStatus = "failed";
        state.patientPrescriptionsError =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la récupération des prescriptions du patient";
      });

    // =====================================================
    // ✅ FETCH BY CONSULTATION (important pour View all sous chaque consultation)
    // =====================================================
    builder
      .addCase(fetchPrescriptionByConsultation.pending, (state) => {
        state.consultationPrescriptionStatus = "loading";
        state.consultationPrescriptionError = null;
      })
      .addCase(fetchPrescriptionByConsultation.fulfilled, (state, action) => {
        state.consultationPrescriptionStatus = "succeeded";
        state.consultationPrescription = action.payload || null;

        // ✅ NEW: stocker par consultationId si possible
        const cid =
          action.meta?.arg?.consultationId != null
            ? String(action.meta.arg.consultationId).trim()
            : "";

        if (cid) {
          state.byConsultation[cid] = action.payload || null;
        }
      })
      .addCase(fetchPrescriptionByConsultation.rejected, (state, action) => {
        state.consultationPrescriptionStatus = "failed";
        state.consultationPrescriptionError =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la récupération de la prescription de la consultation";

        // ✅ on peut aussi marquer cache = null pour cette consultation si on veut
        const cid =
          action.meta?.arg?.consultationId != null
            ? String(action.meta.arg.consultationId).trim()
            : "";

        if (cid && state.byConsultation[cid] === undefined) {
          state.byConsultation[cid] = null;
        }
      });

    // =====================================================
    // UPDATE
    // =====================================================
    builder
      .addCase(updatePrescription.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updatePrescription.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updated = action.payload;
        if (!updated?.id) return;

        const idx = state.prescriptions.findIndex((p) => p.id === updated.id);
        if (idx !== -1) state.prescriptions[idx] = updated;

        const idxPatient = state.patientPrescriptions.findIndex((p) => p.id === updated.id);
        if (idxPatient !== -1) state.patientPrescriptions[idxPatient] = updated;

        const idxDocPatient = state.doctorPatientPrescriptions.findIndex((p) => p.id === updated.id);
        if (idxDocPatient !== -1) state.doctorPatientPrescriptions[idxDocPatient] = updated;

        // ✅ NEW: sync cache
        if (updated?.consultationId) {
          state.byConsultation[String(updated.consultationId)] = updated;
        }
      })
      .addCase(updatePrescription.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la mise à jour de la prescription";
      });

    // =====================================================
    // DELETE
    // =====================================================
    builder
      .addCase(deletePrescription.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deletePrescription.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.payload?.id;
        if (!deletedId) return;

        // remove from lists
        state.prescriptions = state.prescriptions.filter((p) => p.id !== deletedId);
        state.patientPrescriptions = state.patientPrescriptions.filter((p) => p.id !== deletedId);
        state.doctorPatientPrescriptions = state.doctorPatientPrescriptions.filter(
          (p) => p.id !== deletedId
        );

        // ✅ remove from cache if exists
        Object.keys(state.byConsultation).forEach((cid) => {
          const val = state.byConsultation[cid];
          if (val?.id === deletedId) delete state.byConsultation[cid];
        });
      })
      .addCase(deletePrescription.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la suppression de la prescription";
      });

    // =====================================================
    // PDF
    // =====================================================
    builder
      .addCase(generatePrescriptionPdf.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(generatePrescriptionPdf.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(generatePrescriptionPdf.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Erreur lors de la génération du PDF";
      });
  },
});

export const {
  resetPrescriptionState,
  clearError,
  clearLastCreated,
  clearLastClassifiedSave,
  clearConsultationPrescriptionCache,
} = prescriptionSlice.actions;

export default prescriptionSlice.reducer;

/* ===================== SELECTORS ===================== */
export const selectPrescriptionStatus = (state) => state.prescription?.status || "idle";
export const selectPrescriptionError = (state) => state.prescription?.error || null;

export const selectLastPrescription = (state) => state.prescription?.lastCreated || null;
export const selectLastClassifiedSave = (state) =>
  state.prescription?.lastClassifiedSave || null;
export const selectClassifiedSaveStatus = (state) =>
  state.prescription?.classifiedSaveStatus || "idle";
export const selectClassifiedSaveError = (state) =>
  state.prescription?.classifiedSaveError || null;

export const selectAllPrescriptions = (state) => state.prescription?.prescriptions || [];
export const selectPrescriptionsStatus = (state) => state.prescription?.prescriptionsStatus || "idle";
export const selectPrescriptionsError = (state) => state.prescription?.prescriptionsError || null;

export const selectPatientPrescriptions = (state) => state.prescription?.patientPrescriptions || [];
export const selectPatientPrescriptionsStatus = (state) =>
  state.prescription?.patientPrescriptionsStatus || "idle";
export const selectPatientPrescriptionsError = (state) =>
  state.prescription?.patientPrescriptionsError || null;

// doctor -> patient
export const selectDoctorPatientPrescriptions = (state) =>
  state.prescription?.doctorPatientPrescriptions || [];
export const selectDoctorPatientPrescriptionsStatus = (state) =>
  state.prescription?.doctorPatientPrescriptionsStatus || "idle";
export const selectDoctorPatientPrescriptionsError = (state) =>
  state.prescription?.doctorPatientPrescriptionsError || null;

// consultation (last fetched)
export const selectConsultationPrescription = (state) =>
  state.prescription?.consultationPrescription || null;
export const selectConsultationPrescriptionStatus = (state) =>
  state.prescription?.consultationPrescriptionStatus || "idle";
export const selectConsultationPrescriptionError = (state) =>
  state.prescription?.consultationPrescriptionError || null;

// ✅ NEW: selector par consultationId (cache)
export const selectPrescriptionByConsultationId = (state, consultationId) => {
  const cid = consultationId != null ? String(consultationId).trim() : "";
  return cid ? state.prescription?.byConsultation?.[cid] ?? undefined : undefined;
};

export const selectTemplate = (state) => state.prescription?.template || null;
export const selectTemplateStatus = (state) => state.prescription?.templateStatus || "idle";
export const selectTemplateError = (state) => state.prescription?.templateError || null;
